import React, { useContext } from 'react';
import Helmet from 'react-helmet';
import Layout from '@/components/layout';
import HeroV2 from '@/components/Hero/HeroV2/HeroV2';
import Section from '@latitude/section-rebrand';
import { Accordion } from '@latitude/accordion-rebrand';
import Typography from '@latitude/typography';
import { PageContext } from '@/context/PageContext';
import { REBRAND_COLOR, PADDING } from '@latitude/core/utils/constants';
import HeroBranded from '@/components/HeroBrandedRebrand/HeroBrandedRebrand';
import FeatureGridV1 from '@/components/FeatureGridV1/FeatureGridV1';
import SvgInline from '@latitude/svg-inline';
import { AnalyticsLocationProvider } from 'latitude-analytics';
import styled from 'styled-components';
import { RewardsCarasoule } from '@/components/RewardsCarasoule/RewardsCarasoule';

const PageData = {
    heroBannerData: [
        {
            slides: [
                {
                    headingCopy: { content: [], data: {} },
                    bodyCopy: { content: [], data: {} },
                    heading: 'Digital Card',
                    body: 'Manage your card instantly with the Latitude app.',
                    image: { file: { url: '/images/mobile-app/hero-digital-card.webp', fileName: 'digital-card' } },
                    timer: 10000
                }
            ],
            background: 'white'
        }
    ],
    features: [
        {
            id: 'f1',
            title: 'Instant activation',
            text: 'Activate your card in seconds using the app',
            icon: 'icon-device-mobile-rebrand'
        },
        {
            id: 'f2',
            title: 'Tap & Pay',
            text: 'Add your card to Apple Pay or Google Pay',
            icon: 'icon-wallet-rebrand'
        },
        {
            id: 'f3',
            title: 'Manage payments',
            text: 'View transactions and make payments on the go',
            icon: 'icon-percent-rebrand'
        }
    ],
    faq: [
        { title: 'How do I get the digital card?', content: 'Download the Latitude App and follow the prompts to add your card.' },
        { title: 'Is the digital card secure?', content: 'Yes — the digital card uses tokenisation and device security.' }
    ]
};

const getFeatureGridDataFromContentful = featureSlider => {
    const richTextCards = featureSlider?.featureCards?.filter(
        card => card?.richText
    );

    if (!richTextCards?.length) return null;

    return {
        features: richTextCards.map(card => ({
            icon: card.icon ? <SvgInline name={card.icon} /> : undefined,
            headingText: card.title,
            richText: card.richText
        }))
    };
};

const FEATURE_GRID_DATA = {
    features: [
        {
            icon: <SvgInline name="icon-percent-rebrand" />,
            headingText: 'Let your budget breathe',
            paragraphText:
                'Enjoy a 13.99% p.a. purchase rate, so you can stretch your budget without stressing it.'
        },
        {
            icon: <SvgInline name="icon-confetti-rebrand" />,
            headingText: 'Earn on everyday spend',
            paragraphText: (
                <>
                    Get 3% back in Latitude Rewards<sup>1</sup> when you set up recurring payments direct with participating utilities, telco,
                    and streaming services.<a href="https://assets.latitudefinancial.com/legals/terms-conditions/lowrate-rewards.pdf" target="_blank" rel="noopener noreferrer" style={{
                        textUnderlineOffset: '3px',
                        color: '#535862',
                        textDecoration: 'underline',
                        borderBottom: 'none'
                    }}> T&Cs apply</a>.
                </>
            ),

        },
        {
            icon: <SvgInline name="icon-users-rebrand" />,
            headingText: 'Team up for even more rewards',
            paragraphText: `Add an additional cardholder at no extra cost and become the ultimate rewards-earning team.`,
            learnMoreUrl:
                'https://latitudefs.zendesk.com/hc/en-au/articles/36591875170577-Additional-Cardholders'
        },
        {
            icon: <SvgInline name="icon-seal-check-rebrand" />,
            headingText: 'Travel with peace of mind',
            paragraphText:
                'Wherever you go, our specialist team keeps your personal info safe from fraud and identity theft.',
            learnMoreUrl: 'https://www.latitudefinancial.com.au/scams-hub/'
        }
    ]
};

const DigitalCardPage = props => {
    const contentfulPageData = useContext(PageContext);
    const state = contentfulPageData?.updatedBody || PageData;
    const featureGridData =
        getFeatureGridDataFromContentful(state?.featureSliderData?.[0]) ||
        FEATURE_GRID_DATA;
    return (
        <Layout location={props.location}>
            <main className="navigation-spacer">
                <Helmet>
                    <title>Digital Card | Latitude</title>
                    <meta name="description" content="Manage your card instantly with the Latitude app." />
                    <link rel="canonical" href={props.canonical || '/digital-card/'} />
                </Helmet>

                <HeroV2 {...state?.heroBannerData?.[0]} />

                <Section id="features"
                    css={`
                    background-color: ${REBRAND_COLOR.WHITE};
                    padding-top: ${PADDING.P32};
                    padding-bottom: ${PADDING.P32};
                    text-align: center;

                    @media (max-width: 768px) { 
                    text-align: left;
                    }

                    .typography-base__h4{
                    margin-bottom: 16px !important;
                    }

                    p.typography-base {
                    margin: 0;
                    }

                    p.typography-base + p.typography-base {
                    margin-top: ${PADDING.P32} !important;
                    }
                `}
                >
                    <Typography.H4 color={REBRAND_COLOR.INDIGO}>How it works</Typography.H4>
                    <Typography.Body color={REBRAND_COLOR.INDIGO}>
                        Your card details are available in the App, so you can shop online, manage subscriptions, make purchases anytime without your physical card, and add your card to your digital wallet for in-store purchases.
                    </Typography.Body>
                    <Typography.Body color={REBRAND_COLOR.INDIGO}>
                        Lost your card? No need to wait for a replacement, your card details are ready when you are.
                    </Typography.Body>
                </Section>

                <Section id="features" css={`background-color: ${REBRAND_COLOR.WHITE}; padding-top: ${PADDING.P32}; padding-bottom: ${PADDING.P32};`}>
                    <FeatureGridV1
                        id="benefits"
                        trackingId="lowrate"
                        {...featureGridData}
                    />
                </Section>

                        <RewardsCarasoule
                          id="app-instruction"
                          title="Access your card details in seconds"
                          subtitle=""
                          rewards={[
                            {
                              imageUrl: require('./images/step-1.webp').default,
                              title: 'Step 1',
                              description: (
                                <span>
                                  Log in to the <strong>Latitude App</strong>, select the card you wish to use and tap the arrow next to ‘Card details’.
                                </span>
                              )
                            },
                            {
                              imageUrl: require('./images/step-2.webp').default,
                              title: 'Step 2',
                              description:
                                (<>Your card details (card number, expiry date and CVV) will pop on up the screen.</>)
                            },
                            {
                              imageUrl: require('./images/step-3.webp').default,
                              title: 'Step 3',
                              description: (<>Simply tap the ‘Copy’ icon to copy your card number when shopping online.</>)
                            }
                          ]}
                        />
                <HeroBranded id="download-app" heading={<>Download the Latitude credit card app</>} subTitle={<>Manage your account, make payments and track your purchases in just a few taps with our mobile app.</>}/>
                <Section>
                    <StyledSection>

                        <Typography.H4 css={`padding-bottom: 32px !important;`}>

                            {state?.faqData?.[0]?.title || 'Frequently asked questions'}
                        </Typography.H4>
                        <AnalyticsLocationProvider
                            location={
                                state?.faqData?.[0]?.title || 'frequently asked questions'
                            }
                        >
                            <Accordion
                                 css={`
                                .bg-black-10 {
                                    padding: 16px 24px;
                                }
                                `}
                                variant="outlined"
                                titleBgColor={REBRAND_COLOR.WHITE}
                                contentBgColor={REBRAND_COLOR.WHITE}
                                fgColor={REBRAND_COLOR.MID_BLUE}
                                items={state?.faqData?.[0]?.data || PageData.faq}
                            />
                        </AnalyticsLocationProvider>
                    </StyledSection>
                </Section>
            </main>
        </Layout>
    );
};

export default DigitalCardPage;


const StyledSection = styled.div`

   padding-top: 64px !important;
   padding-bottom: 32px !important;

  .select-list__item {
    border-top: 1px solid #ccddfd !important;
    border-bottom: 1px solid #ccddfd !important;
    color: ${REBRAND_COLOR.INDIGO};
  }


  .typography-base__cta--bold,
  .typography-base__cta--bold {
    color: ${REBRAND_COLOR.INDIGO} !important;
    font-weight: 600;
  }

  [class*="Text__StyledText-"] {
    color: ${REBRAND_COLOR.INDIGO} !important;
    font-weight: 500;
  }
  .accordion__item > .link-block.bg-stone {
    background-color: white !important;
    border: 1px solid var(--tint-black-40, #a4a7ae) !important;
    border-radius: 8px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  }

  .accordion__item .bg-black-10 {
    padding: 16px;
    border: 1px solid var(--tint-black-40, #a4a7ae) !important;
    border-radius: 8px;
    border-top: none;
  }
`;